import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-02-19 12:26:35 +0100 (Thu, 19 Feb 2026) $
//  Author    : Dirk Rothe, Olli Powroznik
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 160857 $
//  Purpose   : wiki parts
// =============================================================================
// ---Third-party imports
import React, { useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
// ---Framework imports
import log from 'uix/logging';
import { Sel, Actions } from 'uix/registries';
import { size, isNil, keys } from 'ldash';
import { formatDistance } from 'uix/utils/date';
import { funcPart } from 'uix/parts';
import { Editor, Differ, mimetype2options } from 'uix/components/editor';
import { XMenu, HeaderSegment, DebugFeature, SplitPane, Toolbar } from 'uix/components';
import { selectDomainClassCfgs } from 'uix/selectors';
import { useActions, useEffect, useSelector, usePersistentShallow } from 'uix/hooks';
// ---Local imports
import { saveWikiText, setEditedWikiCode } from '../actions';
import { WikiPreview, KeyMap, WikiMimetype, ChildResourceUpload, WikiAlternatives } from '../components';
import { WikiInfoPane } from '../codeinfo';
import { WebdavUpload } from '../webdav';
import { MIME } from '../utils';
import { buildCompleter, buildWikiTemplates } from '../hooks';
// ---Globals
const editTrackerStoreKey = ({ otid, dname, identifier })=>otid || `${dname}_${identifier}`;
const WikiEditTracker = ({ otid, dname, identifier, text, updateTS })=>{
    let set = useActions(setEditedWikiCode);
    let [bouncedText] = useDebounce(text, 2000) // we force also on unmount?
    ;
    useEffect(()=>{
        if (!isNil(bouncedText)) {
            set({
                text: bouncedText,
                storeKey: editTrackerStoreKey({
                    otid,
                    dname,
                    identifier
                }),
                remoteUpdateTs: updateTS
            });
        }
    }, [
        otid,
        dname,
        identifier,
        bouncedText,
        updateTS
    ]);
    return null;
};
export const WikiEditor = funcPart({
    getChunkedUtils: [
        'EditorUtils'
    ],
    filters: [
        {
            type: 'getWikiCode',
            resolve: true,
            paramsFromPart: [
                'identifier',
                'id'
            ]
        }
    ],
    partStyle: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%'
    }
}, (props)=>{
    let { wikiCode, EditorUtils, dname, go, partcfg, language } = props;
    let [ui, setUI] = usePersistentShallow('wikiEditorOptions', {
        activeIndex: 0,
        lineWrapping: true,
        previewActive: true,
        editorInfoArea: false,
        uploadActive: false,
        webdavUploadActive: false,
        __version__: 12
    });
    let domainDescription = useSelector(Sel.domainDescription);
    let jobSpec = useSelector((root)=>root.config.jobSpec);
    let classCfg = useSelector(selectDomainClassCfgs);
    let wikiEdited = useSelector((root)=>root.data.wiki.edited);
    let widgetSchema = useSelector((root)=>root.config.widgetSchema);
    let [saveWikiTextAction, setEditedWikiCodeAction, callApi] = useActions([
        saveWikiText,
        setEditedWikiCode,
        Actions.callApi
    ]);
    let cmRef = useRef(null);
    let [state, setState] = useState({
        identifier: null,
        otid: null,
        updateTS: null,
        editorCursorToken: null,
        diffActive: false,
        source: undefined,
        changesPending: false,
        changedSource: undefined,
        editorCursor: undefined,
        editorScroll: undefined,
        language: null
    });
    let deriveState = (prev)=>{
        let nextState = {};
        if (!wikiCode) {
            return nextState;
        }
        let { text, identifier, otid, updateTS, dname, mimetype, datatype } = wikiCode;
        let identChanged = prev.identifier !== identifier;
        let idChanged = prev.otid !== otid;
        let tsChanged = prev.updateTS !== updateTS;
        let languageChange = prev.language !== language;
        if (identChanged || idChanged || tsChanged) {
            let resetEditor = identChanged || prev.otid !== null && idChanged;
            nextState = {
                ...nextState,
                identifier,
                otid,
                updateTS,
                source: text,
                changesPending: false,
                changedSource: undefined,
                editorCursor: resetEditor ? {
                    line: 0,
                    ch: 0
                } : prev.editorCursor,
                editorScroll: resetEditor ? {
                    x: 0,
                    y: 0
                } : prev.editorScroll,
                editorCursorToken: null,
                language
            };
        } else if (languageChange) {
            nextState = {
                ...nextState,
                language
            };
        }
        if (isNil(nextState.changedSource ?? prev.changedSource)) {
            let lastEditOp = wikiEdited[editTrackerStoreKey({
                otid,
                dname,
                identifier
            })];
            if (lastEditOp && (!updateTS || lastEditOp.remoteUpdateTs >= updateTS)) {
                let changesPending = text != lastEditOp.text;
                if (changesPending) {
                    nextState = {
                        ...nextState,
                        changedSource: lastEditOp.text,
                        changesPending
                    };
                }
            }
        }
        return nextState;
    };
    useEffect(()=>{
        setState((prev)=>{
            let nextState = deriveState(prev);
            if (size(keys(nextState)) === 0) {
                return prev;
            }
            return {
                ...prev,
                ...nextState
            };
        });
    }, [
        wikiCode,
        language,
        wikiEdited
    ]);
    let partName = ()=>partcfg.storeKey || props.name || props.type;
    let mountEditor = (editor)=>{
        cmRef.current = editor;
    };
    let saveWikiTextLocal = ()=>{
        if (!state.changesPending) {
            log.warn('nothing to save');
            return;
        }
        let args = {
            text: state.changedSource ?? '',
            partName: partName(),
            dname
        };
        if (wikiCode?.otid) {
            args.otid = wikiCode.otid;
        } else if (wikiCode?.identifier) {
            args.identifier = wikiCode.identifier;
        } else {
            log.warn('no otid or identifier');
            return;
        }
        if (wikiCode?.missing) {
            args.mimetype = wikiCode.mimetype;
        }
        saveWikiTextAction(args);
    };
    let codeChange = ({ value: text })=>{
        setState((prev)=>({
                ...prev,
                changesPending: prev.source !== text,
                changedSource: text
            }));
    };
    let cursorChange = ({ editor, data })=>{
        let editorCursorToken = editor.getTokenAt(editor.getCursor());
        setState((prev)=>({
                ...prev,
                editorCursor: data,
                editorCursorToken
            }));
    };
    let editorScrollChange = ({ editor, data })=>{
        setState((prev)=>({
                ...prev,
                editorScroll: data
            }));
    };
    let webdavUploadAvailable = ()=>wikiCode?.wikihub;
    let getCurrentText = ()=>state.changedSource || wikiCode?.text;
    let prettyPrint = ()=>{
        let text = getCurrentText();
        if (!wikiCode) {
            return;
        }
        callApi('prettyPrint', {
            text,
            mimetype: wikiCode.mimetype
        }, ({ prettyData })=>{
            setState((prev)=>({
                    ...prev,
                    changedSource: prettyData,
                    changesPending: prettyData != wikiCode.text
                }));
        }, {
            method: 'post'
        });
    };
    let localEditorMenu = ()=>{
        let { mimetype } = wikiCode;
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    active: ui.lineWrapping,
                    onClick: ()=>setUI({
                            lineWrapping: !ui.lineWrapping
                        }),
                    icon: "linewrap",
                    title: "Textwrap"
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    onClick: ()=>{},
                    icon: "keyboard outline",
                    confirm: /*#__PURE__*/ _jsx(KeyMap, {
                        keymap: EditorUtils.getKeyMap()
                    })
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    disabled: !cmRef.current,
                    active: ui.editorInfoArea,
                    onClick: ()=>setUI({
                            editorInfoArea: !ui.editorInfoArea
                        }),
                    icon: "help",
                    title: "Show Infopane"
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    position: "right",
                    disabled: mimetype == MIME.MARKDOWN || mimetype == MIME.HTML,
                    onClick: prettyPrint,
                    icon: "prettyprint",
                    title: "Pretty Print"
                })
            ]
        });
    };
    let editorMenu = (mode)=>{
        let { mimetype } = wikiCode;
        let revert = ()=>{
            setEditedWikiCodeAction({
                text: undefined,
                storeKey: editTrackerStoreKey(wikiCode)
            });
            setState((prev)=>({
                    ...prev,
                    changesPending: false,
                    changedSource: undefined
                }));
        };
        let confirmNew = /*#__PURE__*/ _jsxs("span", {
            children: [
                'Create new wikipage ',
                /*#__PURE__*/ _jsx("b", {
                    children: /*#__PURE__*/ _jsx("tt", {
                        children: wikiCode.identifier
                    })
                }),
                ' in domain: ',
                /*#__PURE__*/ _jsx("b", {
                    children: domainDescription
                })
            ]
        });
        return /*#__PURE__*/ _jsxs(XMenu, {
            children: [
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    icon: "save",
                    disabled: !state.changesPending,
                    onClick: saveWikiTextLocal,
                    confirm: wikiCode.otid ? undefined : confirmNew,
                    title: "Save (Strg+S)"
                }),
                webdavUploadAvailable() && /*#__PURE__*/ _jsx(XMenu.Item, {
                    icon: "upload",
                    active: ui.webdavUploadActive,
                    onClick: ()=>setUI({
                            webdavUploadActive: !ui.webdavUploadActive
                        }),
                    title: "Upload to help.vls.io"
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    icon: [
                        'undo',
                        {
                            name: 'lightning',
                            color: 'red'
                        }
                    ],
                    disabled: !state.changesPending,
                    onClick: revert,
                    title: "Revert",
                    confirm: "Revert all changes to last saved state?"
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    disabled: !state.changesPending,
                    active: state.diffActive,
                    onClick: ()=>setState((prev)=>({
                                ...prev,
                                diffActive: !prev.diffActive
                            })),
                    icon: "exchange",
                    title: "Diff"
                }),
                wikiCode.otid && /*#__PURE__*/ _jsx(XMenu.Item, {
                    active: ui.uploadActive,
                    onClick: ()=>setUI({
                            uploadActive: !ui.uploadActive
                        }),
                    icon: "upload",
                    title: "Upload resources"
                }),
                /*#__PURE__*/ _jsx(XMenu.Separator, {}),
                !ui.previewActive && localEditorMenu(),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    disabled: !cmRef.current,
                    active: ui.previewActive,
                    onClick: ()=>setUI({
                            previewActive: !ui.previewActive
                        }),
                    icon: "columns",
                    title: "Preview"
                }),
                /*#__PURE__*/ _jsx(DebugFeature, {
                    children: /*#__PURE__*/ _jsxs(XMenu.SubMenu, {
                        icon: "cog",
                        content: "More",
                        children: [
                            /*#__PURE__*/ _jsx(WikiMimetype, {
                                icon: "configure",
                                otid: wikiCode.otid,
                                datatype: wikiCode.datatype,
                                mimetype: mimetype,
                                onClick: ()=>{}
                            }),
                            wikiCode.fspath && /*#__PURE__*/ _jsx(XMenu.Item, {
                                content: wikiCode.fspath
                            }),
                            /*#__PURE__*/ _jsx(XMenu.Item, {
                                onClick: ()=>go({
                                        classname: 'tree',
                                        tabname: 'id',
                                        id: wikiCode.otid
                                    }),
                                icon: "link",
                                disabled: !wikiCode.otid,
                                content: `Goto ${wikiCode.otid}`
                            })
                        ]
                    })
                })
            ]
        });
    };
    let renderEditor = ()=>{
        if (!wikiCode) {
            return null;
        }
        let { source, changedSource, editorCursor, editorScroll, identifier, editorCursorToken } = state;
        let { otid, mimetype } = wikiCode;
        let { mode, options: modeOptions } = mimetype2options(mimetype);
        let env = {
            classCfg,
            jobSpec
        };
        let completion = buildCompleter(buildWikiTemplates(widgetSchema), EditorUtils, env, mimetype);
        let opts = {
            mode,
            lineWrapping: ui.lineWrapping,
            autofocus: false,
            lineNumbers: wikiCode.datatype === 18,
            showTrailingSpace: true,
            tabSize: 2,
            hintOptions: completion.hintOptions
        };
        let extraKeys = {
            'Ctrl-S': saveWikiTextLocal,
            ...completion.extraKeys
        };
        let wrapWithInfo = (editor)=>/*#__PURE__*/ _jsxs(SplitPane, {
                persist: true,
                name: "InfoSplitPane",
                sizes: [
                    0.8,
                    0.2
                ],
                styles: [
                    {
                        overflow: 'hidden',
                        display: 'flex'
                    },
                    {
                        overflowX: 'hidden'
                    }
                ],
                borderless: ui.previewActive,
                children: [
                    editor,
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            overflow: 'auto'
                        },
                        children: /*#__PURE__*/ _jsx(WikiInfoPane, {
                            cmMode: mode,
                            identifier: wikiCode.identifier,
                            changedTs: wikiCode.updateTS,
                            cmToken: editorCursorToken
                        }, `${wikiCode.identifier}_${otid}`)
                    })
                ]
            });
        let editor = null;
        if (!state.diffActive) {
            editor = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    !isNil(changedSource) ? /*#__PURE__*/ _jsx(WikiEditTracker, {
                        ...wikiCode,
                        text: changedSource
                    }) : null,
                    /*#__PURE__*/ _jsx(Editor, {
                        flex: true,
                        mimetype: mimetype,
                        bordered: !ui.previewActive && !ui.editorInfoArea,
                        text: changedSource ?? source,
                        opts: opts,
                        extraKeys: extraKeys,
                        onBeforeChange: codeChange,
                        editorDidMount: mountEditor,
                        onCursor: cursorChange,
                        cursor: editorCursor,
                        onScroll: editorScrollChange,
                        scroll: editorScroll,
                        onContextMenu: ()=>{}
                    })
                ]
            });
        } else {
            editor = /*#__PURE__*/ _jsx(Differ, {
                flex: true,
                textLeft: source,
                textMiddle: changedSource ?? '',
                mode: mode.name,
                xml: modeOptions.xml,
                lineNumbers: opts.lineNumbers,
                lineWrapping: opts.lineWrapping,
                onChange: codeChange
            });
        }
        if (ui.editorInfoArea) {
            editor = wrapWithInfo(editor);
        }
        if (!ui.previewActive) {
            return editor;
        }
        return /*#__PURE__*/ _jsxs(SplitPane, {
            vertical: true,
            persist: true,
            name: "PreviewSplitPane",
            flexInner: true,
            children: [
                /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Toolbar, {
                            attached: "top",
                            size: "small",
                            children: localEditorMenu()
                        }),
                        editor
                    ]
                }),
                /*#__PURE__*/ _jsx(WikiPreview, {
                    source: changedSource ?? source,
                    otid: otid,
                    identifier: identifier,
                    datataype: wikiCode.datatype,
                    mimetype: wikiCode.mimetype,
                    showMenu: true
                })
            ]
        });
    };
    let webdavUpload = ()=>{
        if (!webdavUploadAvailable() || !ui.webdavUploadActive) return null;
        return /*#__PURE__*/ _jsx(WebdavUpload, {
            identifier: wikiCode.identifier,
            mimetype: wikiCode.mimetype,
            text: getCurrentText()
        });
    };
    let renderSegment = ()=>{
        let subheader = null;
        let content = null;
        let menu = null;
        if (wikiCode) {
            content = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    webdavUpload(),
                    renderEditor()
                ]
            });
            let { otid } = wikiCode;
            if (otid && wikiCode.updateTS) {
                subheader = `changed: ${formatDistance(wikiCode.updateTS)} ago`;
            }
            menu = editorMenu('sui');
        }
        return /*#__PURE__*/ _jsx(HeaderSegment, {
            major: true,
            loading: !wikiCode,
            flex: true,
            icon: "edit",
            header: "Editor",
            subheader: subheader,
            menu: menu,
            fullscreenable: true,
            children: content
        });
    };
    if (!partcfg.bare) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                renderSegment(),
                partcfg.showAlternatives && /*#__PURE__*/ _jsx(WikiAlternatives, {}),
                ui.uploadActive && /*#__PURE__*/ _jsx(ChildResourceUpload, {
                    otid: state.otid
                })
            ]
        });
    }
    return renderEditor();
}) //~ export default WikiEditor
;
